import { type MessageStatus as Status, statusLabel } from '@fundline/shared/messages/status';

/** Stroked 16×16 icons per status (ticks like a phone's messages app). */
const PATHS: Partial<Record<Status, string>> = {
  queued: 'M8 4v4l2.5 1.5M8 14.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13',
  held: 'M6 4.5v7M10 4.5v7',
  sending: 'M3 8.5l3 3 7-7',
  sent: 'M3 8.5l3 3 7-7',
  delivered: 'M1.5 8.5l3 3 7-7M7 11.5l7-7',
  failed: 'M8 4v5M8 11.5v.5M8 14.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13',
  unknown:
    'M6 6a2 2 0 1 1 2.8 1.8c-.5.3-.8.7-.8 1.2v.5M8 11.5v.5M8 14.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13',
  blocked: 'M3.5 12.5l9-9M8 14.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13',
};

/** Delivered is a success state (mint); problems are red, waiting is amber, the rest stays muted. */
const TONE: Record<string, string> = {
  neutral: 'text-ink-muted',
  progress: 'text-ink-muted',
  ok: 'text-success',
  warning: 'text-warning',
  error: 'text-danger',
};

/** The tick marks and label under an outbound text; `iconOnly` in inbox rows (label kept for readers). */
export function MessageStatusTicks({
  status,
  detail,
  iconOnly = false,
}: {
  status: Status;
  detail?: string;
  iconOnly?: boolean;
}) {
  const { label, tone } = statusLabel(status);
  const d = PATHS[status];
  return (
    <span
      className={`footnote inline-flex shrink-0 items-center gap-1 ${TONE[tone] ?? ''}`}
      data-testid="message-status"
      data-status={status}
    >
      {d ? (
        <svg
          viewBox="0 0 16 16"
          className={`size-3.5 ${status === 'sending' ? 'opacity-50' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d={d} />
        </svg>
      ) : null}
      <span className={iconOnly ? 'sr-only' : undefined}>
        {label}
        {detail ? ` · ${detail}` : ''}
      </span>
    </span>
  );
}
